import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * 错误边界组件
 * 捕获子组件中的 JavaScript 错误，防止整个应用崩溃
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // 更新 state 使下一次渲染显示降级 UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 可以将错误日志上报给服务器
    console.error('错误边界捕获到错误:', error, errorInfo);
    
    // 如果提供了 onError 回调，则调用它
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // 如果提供了自定义的降级 UI，则使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // 默认的降级 UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-container">
            <h2>抱歉，组件出现了问题</h2>
            <p>请尝试刷新页面或联系管理员</p>
            {this.state.error && (
              <details style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
                <summary>查看详细错误信息</summary>
                <p>{this.state.error.toString()}</p>
              </details>
            )}
            <button 
              onClick={() => window.location.reload()}
              style={{
                marginTop: '20px',
                padding: '8px 16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              刷新页面
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 